import { useEffect, useRef } from 'react';
import type { StorageSasMintedEvent, StorageSasRequestedEvent } from '../../../../../types/azureStorage';
import { onStorageSasMinted } from '../../services/azureStorageApi';
import { useStorageDialogStore } from '../dialogs/storageDialogStore';

export type SasApprovalEvent = { requestId: string };
type Subscribe<T> = (callback: (event: T) => void) => () => void;

type ApprovalApi = {
  mcpUiControl?: { onStorageWriteApprovalRequest?: Subscribe<SasApprovalEvent> };
};

const subscribeApprovalByDefault: Subscribe<SasApprovalEvent> = (callback) =>
  (window.electronAPI as typeof window.electronAPI & ApprovalApi)?.mcpUiControl?.onStorageWriteApprovalRequest?.(callback) ?? (() => {});
const subscribeRequestedByDefault: Subscribe<StorageSasRequestedEvent> = (callback) =>
  window.electronAPI?.azureStorage?.onSasRequested?.(callback) ?? (() => {});

/**
 * Opens the SAS result panel for an MCP-minted grant exactly once, and only for an id the renderer learned first:
 * from S2's storage write-approval request (`confirm`) or from S3's own announce for a request that needed no
 * approval (review C D4). Unknown and repeated ids are ignored.
 */
export function useSasMintedEvents(overrides?: {
  subscribeApproval?: Subscribe<SasApprovalEvent>;
  subscribeRequested?: Subscribe<StorageSasRequestedEvent>;
  subscribeMinted?: Subscribe<StorageSasMintedEvent>;
}): void {
  const expectedIds = useRef(new Set<string>());
  useEffect(() => {
    const subscribeApproval = overrides?.subscribeApproval ?? subscribeApprovalByDefault;
    const subscribeRequested = overrides?.subscribeRequested ?? subscribeRequestedByDefault;
    const subscribeMinted = overrides?.subscribeMinted ?? onStorageSasMinted;
    const expect = (event: { requestId: string }) => expectedIds.current.add(event.requestId);
    const stopApproval = subscribeApproval(expect);
    const stopRequested = subscribeRequested(expect);
    const stopMinted = subscribeMinted((event) => {
      if (!expectedIds.current.delete(event.requestId)) return;
      useStorageDialogStore.getState().openDialog({
        kind: 'sas', connectionId: '', resource: event.grant.resource, source: 'mcp', grant: event.grant,
      });
    });
    return () => {
      stopApproval();
      stopRequested();
      stopMinted();
      expectedIds.current.clear();
    };
  }, [overrides?.subscribeApproval, overrides?.subscribeRequested, overrides?.subscribeMinted]);
}
